import React, { useEffect } from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { Link, Switch, Route, withRouter } from "react-router-dom";
import { Helmet } from "react-helmet";

import ItemLog from "../../components/common/ItemLog";
import Pagination from "../../components/common/Pagination";

import ReportTable from "../../components/Orders/ReportTable";
import ReportDetail from "../../components/Orders/ReportDetail";
import {
  ReportCreateForm,
  ReportUpdateForm,
} from "../../components/Orders/ReportForm";
import { VetoReviewUpdateForm } from "../../components/Orders/VetoReviewForm";

import {
  fetchOrder,
  vetoApprove,
  vetoReject,
  vetoSection,
} from "../../actions/orders";
import VetoReviewSectionsForm from "../../components/Orders/VetoReviewSectionsForm";
import {
  fetchReports,
  fetchReport,
  createReport,
  updateReport,
} from "../../actions/reports";
import { isVeto, isLeadAuditor, isAuditor } from "../../selectors/order";

export function OrderReportView(props) {
  const {
    accessToken,
    fetchOrder,
    fetchReport,
    fetchReports,
    createReport,
    updateReport,
    match,
    push,
    order,
    user,
    report,
    reports,
    vetoApprove,
    vetoReject,
    vetoSection,
  } = props;

  const { orderId, id } = match.params;

  useEffect(() => {
    fetchOrder(orderId, accessToken);
  }, [orderId, accessToken, fetchOrder]);

  useEffect(() => {
    if (id !== "new") {
      fetchReport(orderId, id, accessToken);
    } else if (!id) {
      fetchReports(orderId, accessToken);
    }
  }, [orderId, id, fetchReport, fetchReports, accessToken]);

  function handleSubmit(e) {
    const request = {
      ...e,
      order_id: orderId,
      status: e.advance ? "Published" : "Draft",
      log: {
        remarks: e.remarks,
      },
    };
    if (id === "new") {
      return createReport(request, accessToken).then(() => {
        if (request.status === "Published") {
          push(`/orders/${orderId}`);
        }
      });
    } else {
      return updateReport({ ...request, id }, accessToken).then(() => {
        if (request.status === "Published") {
          push(`/orders/${orderId}`);
        }
      });
    }
  }

  if (!order) {
    return <p>loading</p>;
  }
  if (id && id !== "new" && !report) {
    return <p>loading</p>;
  }

  return (
    <div className="content">
      <Switch>
        <Route path="/orders/:orderId/reports/new">
          <div>
            <Helmet>
              <title>{`Order ${order.number} - Create report`}</title>
            </Helmet>
            <div className="main-header">
              <h2>
                {order.status === "Veto Person Assigned"
                  ? "Veto Review"
                  : "New Report"}
              </h2>
              <em>
                For Order <Link to={`/orders/${order.id}`}>{orderId}</Link>
              </em>
            </div>
            <div className="row">
              <div className="col-md-8">
                <div className="alert alert-info">
                  Current Order status is <strong>{order.status}</strong>
                </div>
                {["Audit Stage 1 On Process"].includes(order.status) &&
                  isLeadAuditor(user, order) && (
                    <ReportCreateForm
                      order={order}
                      initialValues={{ type: "Stage 1" }}
                      onSubmit={handleSubmit}
                      fileAPI={{
                        url: process.env.REACT_APP_BASE_API,
                        accessToken: accessToken,
                      }}
                    />
                  )}
                {["Audit Stage 2 On Process"].includes(order.status) &&
                  isLeadAuditor(user, order) && (
                    <ReportCreateForm
                      order={order}
                      initialValues={{ type: "Stage 2" }}
                      onSubmit={handleSubmit}
                      fileAPI={{
                        url: process.env.REACT_APP_BASE_API,
                        accessToken: accessToken,
                      }}
                    />
                  )}
                {["Audit On Process"].includes(order.status) &&
                  isLeadAuditor(user, order) && (
                    <ReportCreateForm
                      order={order}
                      initialValues={{ type: "Audit" }}
                      onSubmit={handleSubmit}
                      fileAPI={{
                        url: process.env.REACT_APP_BASE_API,
                        accessToken: accessToken,
                      }}
                    />
                  )}

                {["Veto Person Assigned"].includes(order.status) && // status
                  isVeto(user, order) && ( // user
                    <VetoReviewSectionsForm
                      order={order}
                      user={user}
                      fileAPI={{
                        url: process.env.REACT_APP_BASE_API,
                        accessToken: accessToken,
                      }}
                      vetoSection={vetoSection}
                      fetchOrder={fetchOrder}
                      push={push}
                    />
                  )}
              </div>
            </div>
          </div>
        </Route>
        <Route path="/orders/:orderId/reports/:id">
          <div>
            <Helmet>
              <title>{`Order ${order.number} - Update report`}</title>
            </Helmet>
            <div className="main-header">
              {report && (
                <>
                  {(() => {
                    const query = new URLSearchParams(props.location.search);
                    const isForcedDetail = query.get("mode") === "detail";

                    const isSuperAdmin = user.data.role.includes("Super Admin");
                    const isEditor =
                      isAuditor(user, order) ||
                      isLeadAuditor(user, order) ||
                      isSuperAdmin;

                    const reportType = report?.type?.toLowerCase();

                    const canUpdateAudit =
                      isEditor &&
                      ["stage 1", "stage 2", "audit"].includes(reportType);

                    // Veto review is section-based (Phase 2); veto OrderReports
                    // are historical aggregate snapshots — always view-only.
                    const canUpdateVeto = false;

                    const titleAction = (!isForcedDetail && (canUpdateAudit || canUpdateVeto)) ? "Update" : "Detail";
                    const reportLabel = reportType === "veto" ? "Veto Review" : "Report";

                    return (
                      <h2>{`${titleAction} ${reportLabel} ${report && report.id}`}</h2>
                    );
                  })()}
                </>
              )}
              <em>
                For Order <Link to={`/orders/${order.id}`}>{order.number}</Link>
              </em>
            </div>
            <div className="row">
              <div className="col-md-8">
                {(() => {
                  const query = new URLSearchParams(props.location.search);
                  const isForcedDetail = query.get("mode") === "detail";

                  const isSuperAdmin = user.data.role.includes("Super Admin");
                  const isEditor =
                    isAuditor(user, order) ||
                    isLeadAuditor(user, order) ||
                    isSuperAdmin;

                  const reportType = report?.type?.toLowerCase();

                  const canUpdateAudit =
                    isEditor &&
                    ["stage 1", "stage 2", "audit"].includes(reportType);

                  // Veto review is section-based (Phase 2); veto OrderReports
                  // are historical aggregate snapshots — always view-only.
                  const canUpdateVeto = false;

                  if (!isForcedDetail) {
                    if (canUpdateAudit) {
                      return (
                        <ReportUpdateForm
                          order={order}
                          onSubmit={handleSubmit}
                          initialValues={report}
                          fileAPI={{
                            url: process.env.REACT_APP_BASE_API,
                            accessToken: accessToken,
                          }}
                        />
                      );
                    }

                    if (canUpdateVeto) {
                      return (
                        <VetoReviewUpdateForm
                          order={order}
                          onSubmit={handleSubmit}
                          initialValues={{ ...report, remarks: "" }}
                          fileAPI={{
                            url: process.env.REACT_APP_BASE_API,
                            accessToken: accessToken,
                          }}
                          vetoApprove={vetoApprove}
                          vetoReject={vetoReject}
                        />
                      );
                    }
                  }

                  // Default to Detail view
                  return (
                    <ReportDetail
                      order={order}
                      report={report}
                      accessToken={accessToken}
                    />
                  );
                })()}
              </div>
              <div className="col-md-4">
                <ItemLog
                  log={report && report.log}
                  entity="Report"
                  accessToken={accessToken}
                />
              </div>
            </div>
          </div>
        </Route>
        <Route path="/orders/:orderId/reports">
          <div>
            <div className="main-header">
              <h2>Reports</h2>
              {order && (
                <em>
                  For Order <Link to={`/orders/${order.id}`}>{order.id}</Link>
                </em>
              )}
            </div>
            {reports && <ReportTable reports={reports} />}
            <Pagination />
          </div>
        </Route>
      </Switch>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    order: state.orders.order.data,
    reports: state.reports.reportList.data,
    report: state.reports.report.data,
    accessToken: state.auth.user.access.access_token,
    user: state.auth.user,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchOrder: (id, accessToken) => dispatch(fetchOrder(id, accessToken)),
    fetchReports: (id, accessToken) => dispatch(fetchReports(id, accessToken)),
    fetchReport: (orderId, id, accessToken) =>
      dispatch(fetchReport(orderId, id, accessToken)),
    createReport: (data, accessToken, target) =>
      dispatch(createReport(data, accessToken, target)),
    updateReport: (data, accessToken, target) =>
      dispatch(updateReport(data, accessToken, target)),
    push: (to) => dispatch(push(to)),
    vetoApprove: (id, formData, accessToken) =>
      dispatch(vetoApprove(id, formData, accessToken)),
    vetoReject: (id, formData, accessToken) =>
      dispatch(vetoReject(id, formData, accessToken)),
    vetoSection: (id, formData, accessToken) =>
      dispatch(vetoSection(id, formData, accessToken)),
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
    // Implement map dispatch to props
  )(OrderReportView)
);
